import * as React from "react";
import { createPortal } from "react-dom";
import type { Invoice, PaymentConfig } from "@alight/contracts";
import { useEnrolmentUiConfig } from "../config";

/**
 * An invoice as a DOCUMENT, for printing.
 *
 * The screen card is a summary — a status chip, a total, a couple of lines —
 * and printing it produced something that was recognisably not an invoice: no
 * organisation, no bill-to, no dates laid out, no payment details.
 *
 * This is the paper version. It carries what a document has to carry to be
 * useful to a family and to whoever files it: who issued it, who owes it, what
 * for, how much, and — while money is owed — how to pay and what reference to
 * quote.
 *
 * Rendered through a PORTAL to <body> and hidden on screen. That is what makes
 * the print rule simple and reliable: hide every direct child of body except
 * this one. The previous attempt tried to hide everything that was not an
 * ancestor-or-descendant of a card buried deep in the layout, which is a
 * selector that cannot be got right — and it printed a mostly empty page.
 */
export function InvoiceDocument({
  invoice,
  bank,
}: {
  invoice: Invoice;
  bank?: PaymentConfig | null;
}) {
  const { siteName } = useEnrolmentUiConfig();
  const paid = invoice.status === "paid";
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;
  const day = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleDateString("en-AU", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "—";

  // Deliberately plain CSS rather than utility classes: this element is printed,
  // and print has no dark mode, no viewport and no design tokens worth
  // inheriting. Black on white, in points, is what a printer wants.
  return createPortal(
    <div className="invoice-print" aria-hidden>
      <div style={{ padding: "18mm 16mm", color: "#111", fontFamily: "Helvetica, Arial, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12mm" }}>
          <div>
            <div style={{ fontSize: "16pt", fontWeight: 700, letterSpacing: "-0.01em" }}>{siteName}</div>
            {bank?.contact_name ? (
              <div style={{ fontSize: "9pt", color: "#555", marginTop: "1mm" }}>
                Enquiries: {bank.contact_name}
              </div>
            ) : null}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "18pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {paid ? "Receipt" : "Invoice"}
            </div>
            <div style={{ fontSize: "11pt", fontFamily: "monospace", marginTop: "1mm" }}>{invoice.number}</div>
          </div>
        </div>

        <hr style={{ border: 0, borderTop: "1.5pt solid #111", margin: "6mm 0" }} />

        <table style={{ width: "100%", fontSize: "10pt", marginBottom: "8mm" }}>
          <tbody>
            <tr>
              <td style={{ verticalAlign: "top", width: "50%" }}>
                <div style={{ color: "#555", textTransform: "uppercase", fontSize: "8pt", letterSpacing: "0.06em" }}>
                  Billed to
                </div>
                <div style={{ fontWeight: 600, marginTop: "1mm" }}>{invoice.parent_name || "—"}</div>
                <div style={{ color: "#333" }}>{invoice.parent_email}</div>
              </td>
              <td style={{ verticalAlign: "top" }}>
                <div style={{ color: "#555", textTransform: "uppercase", fontSize: "8pt", letterSpacing: "0.06em" }}>
                  Details
                </div>
                <div style={{ marginTop: "1mm" }}>Issued {day(invoice.issued_at)}</div>
                {paid ? <div>Paid {day(invoice.paid_at)}</div> : null}
                {invoice.payment_ref ? (
                  <div>
                    Reference <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{invoice.payment_ref}</span>
                  </div>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10.5pt" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", borderBottom: "1pt solid #111", padding: "2mm 0", fontSize: "8pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#555" }}>
                Description
              </th>
              <th style={{ textAlign: "right", borderBottom: "1pt solid #111", padding: "2mm 0", fontSize: "8pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#555" }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id}>
                <td style={{ padding: "2.5mm 0", borderBottom: "0.5pt solid #ddd" }}>{l.description}</td>
                <td style={{ padding: "2.5mm 0", borderBottom: "0.5pt solid #ddd", textAlign: "right", whiteSpace: "nowrap" }}>
                  {money(l.amount_cents)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ padding: "3mm 0", fontWeight: 700 }}>{paid ? "Paid in full" : "Total due"}</td>
              <td style={{ padding: "3mm 0", textAlign: "right", fontWeight: 700, fontSize: "13pt", whiteSpace: "nowrap" }}>
                {money(invoice.total_cents)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Payment details belong on a bill, never on a receipt — asking again
            for money already received is the one thing a receipt must not do. */}
        {!paid && bank?.bank_account_number ? (
          <div style={{ marginTop: "8mm", padding: "5mm", border: "0.5pt solid #999", fontSize: "10pt" }}>
            <div style={{ fontWeight: 700, marginBottom: "2mm" }}>How to pay</div>
            <div>Account name: {bank.bank_account_name}</div>
            <div>
              BSB: <strong>{bank.bank_bsb}</strong> &nbsp; Account: <strong>{bank.bank_account_number}</strong>
            </div>
            {invoice.payment_ref ? (
              <div style={{ marginTop: "1.5mm" }}>
                Reference: <strong style={{ fontFamily: "monospace" }}>{invoice.payment_ref}</strong>
              </div>
            ) : null}
            <div style={{ marginTop: "2mm", color: "#555", fontSize: "9pt" }}>
              Please include the reference so we can match your payment.
            </div>
          </div>
        ) : null}

        {invoice.note ? (
          <div style={{ marginTop: "6mm", fontSize: "9pt", color: "#555" }}>{invoice.note}</div>
        ) : null}

        <div style={{ marginTop: "12mm", fontSize: "8.5pt", color: "#777", borderTop: "0.5pt solid #ddd", paddingTop: "3mm" }}>
          {siteName} · {invoice.number}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Screen hides it, print shows it and hides everything else. Both rules are
 * one selector deep, which is the whole reason this lives in a portal.
 */
export const INVOICE_PRINT_CSS = `
.invoice-print { display: none; }
@media print {
  body > *:not(.invoice-print) { display: none !important; }
  .invoice-print { display: block !important; }
  @page { margin: 0; size: A4; }
}`;
